/**
 * Streaming transport for the Solomon chat edge function.
 *
 * The general-purpose `invokeEdgeFunction` pipeline buffers the whole
 * response (`response.text()`), so it can't consume Server-Sent Events.
 * This is a focused, opt-in client that POSTs `{ stream: true }` and reads
 * the SSE body incrementally, invoking `onDelta` for each text chunk.
 *
 * It is intentionally minimal — auth header + apikey + base URL only. The
 * caller is expected to FALL BACK to the buffered `invokeEdgeFunction`
 * call if this throws before any text arrives, so a streaming failure
 * degrades to the existing behavior rather than breaking Solomon.
 */
import { supabase } from "@/integrations/supabase/client";
import { getTenantQueryKey } from "@/lib/tenant-query-key";

interface SolomonStreamMessage {
  role: "user" | "assistant";
  content: string;
}

interface StreamSolomonChatArgs {
  messages: SolomonStreamMessage[];
  context?: { page?: string; organizationId?: string; source?: string };
  /** Aborts the in-flight stream (e.g. when a newer request supersedes it). */
  signal?: AbortSignal;
  /** Called with each incremental text chunk as it arrives. */
  onDelta: (text: string) => void;
}

const getBaseUrl = (): string => {
  const raw = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim() ?? "";
  const clean = raw.replace(/\/+$/, "");
  if (!clean) throw new Error("VITE_SUPABASE_URL is not configured.");
  return clean;
};

const getAnonKey = (): string | undefined =>
  (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined) ??
  (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined);

export function buildSolomonStreamHeaders(
  accessToken: string | undefined,
  anonKey: string | undefined,
  supportSessionId: string | null | undefined,
): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (anonKey) headers.apikey = anonKey;
  if (supportSessionId) headers["x-support-session-id"] = supportSessionId;
  return headers;
}

interface SsePayload {
  type: "delta" | "done" | "error";
  text?: string;
  message?: string;
  code?: string;
}

// Match the buffered edge-function default so a stalled stream can degrade to
// the existing non-streaming path instead of leaving the composer disabled.
const STREAM_IDLE_TIMEOUT_MS = 15_000;

const createStreamTimeoutError = () => {
  const error = new Error(
    `Solomon stream request timed out after ${STREAM_IDLE_TIMEOUT_MS}ms without receiving data.`,
  );
  (error as { status?: number }).status = 408;
  return error;
};

/**
 * Stream a Solomon answer. Resolves with the full accumulated text once the
 * server sends `done`. Throws on transport failure, a server `error` frame,
 * or abort — callers should treat a throw *before any onDelta* as a signal
 * to retry via the buffered endpoint.
 */
export async function streamSolomonChat({
  messages,
  context,
  signal,
  onDelta,
}: StreamSolomonChatArgs): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();

  const anonKey = getAnonKey();
  const [, , supportSessionId] = getTenantQueryKey();
  const headers = buildSolomonStreamHeaders(
    session?.access_token,
    anonKey,
    supportSessionId,
  );

  const controller = new AbortController();
  let timedOut = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const clearIdleTimeout = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const resetIdleTimeout = () => {
    clearIdleTimeout();
    timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, STREAM_IDLE_TIMEOUT_MS);
  };

  const abortFromCaller = () => controller.abort(signal?.reason);
  if (signal?.aborted) {
    abortFromCaller();
  } else {
    signal?.addEventListener("abort", abortFromCaller, { once: true });
  }

  try {
    resetIdleTimeout();
    const response = await fetch(`${getBaseUrl()}/functions/v1/solomon-chat`, {
      method: "POST",
      headers,
      body: JSON.stringify({ messages, context, stream: true }),
      signal: controller.signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Solomon stream request failed (${response.status}).`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let full = "";
    let serverError: string | null = null;
    let streamDone = false;

    // SSE frames are separated by a blank line ("\n\n"). We buffer partial
    // chunks across reads and only parse complete frames.
    while (!streamDone) {
      resetIdleTimeout();
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";

      for (const frame of frames) {
        const dataLine = frame.split("\n").find((line) => line.startsWith("data:"));
        if (!dataLine) continue;
        const json = dataLine.slice(5).trim();
        if (!json) continue;

        let payload: SsePayload;
        try {
          payload = JSON.parse(json) as SsePayload;
        } catch {
          continue; // ignore malformed frame
        }

        if (payload.type === "delta" && payload.text) {
          full += payload.text;
          onDelta(payload.text);
        } else if (payload.type === "error") {
          serverError = payload.message ?? "Solomon ran into a problem.";
          streamDone = true;
        } else if (payload.type === "done") {
          streamDone = true;
        }
      }
    }

    if (streamDone) {
      await reader.cancel().catch(() => undefined);
    }

    if (serverError) throw new Error(serverError);
    return full;
  } catch (error) {
    if (timedOut) throw createStreamTimeoutError();
    throw error;
  } finally {
    clearIdleTimeout();
    signal?.removeEventListener("abort", abortFromCaller);
  }
}
